/* Built In Imports */
import config from '@config';
/* External Imports */
import { Box, Checkbox, Flex, SimpleGrid } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { orderByDistance } from 'geolib';
import { BiSearch } from 'react-icons/bi';
import { MdKeyboardArrowDown } from 'react-icons/md';
import Select, { components } from 'react-select';

/* Internal Imports */
/* Components */
import SingleLineText from '@components/UI/SingleLineText';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import Loader from '@components/Utility/Loader';
import { fetchApiAndCache } from '@services/othersService';
import { getLatLong } from '@Services/othersService';

/* Configs */

/* Styles */
/* Services */
import { mixPanel } from '@utils/Mixpanel';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import SimpleButton from '../../components/Buttons/SimpleButton';
import StructuredTextContent from '../../components/UI/StructuredTextContent';
import { ResearchIntroSt } from '../../landing_pages/ieo/Common/CompLangConfig';
import UpcomingProgramCard from './UpcomingProgramCard';

/**
 *
 * @param {Object} sectionContent
 * @param {string} region
 * @param {string} lang
 * @returns {ReactElement} component
 */

const ProgramList = ({ sectionContent, lang, region, pageConfig }) => {
  const pathname = usePathname();

  const apiTakingLongerMessage = 'reload';
  const router = useRouter();
  const Mixpanel = mixPanel(pathname);

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [maxShowDataLength, setMaxShowDataLength] = useState(12);
  const [lastAddedFilterName, setLastAddedFilterName] = useState('');
  const isWavesOfBlissBengaluruPage = pathname.endsWith(
    'wave-of-bliss-bengaluru'
  );
  const [filterValue, setFilterValue] = useState({
    center: null,
    city: null,
    language: null,
    weekdays: null,
    showNearMe: false,
    sadhguruSannidhi: false,
  });

  const upcomingPrograms = useMemo(
    () => pageConfig?.[0]?.value?.upcomingprograms?.[0] || {},
    [pageConfig]
  );

  useEffect(() => {
    if (router.asPath.includes('#ieo-wave-program-registration')) {
      setTimeout(() => {
        document
          .querySelector('#ieo-wave-program-registration')
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
      }, 1000);
    }
  }, [router.asPath]);

  function findField(fieldId) {
    const data = sectionContent.find(el => el._modelApiKey === fieldId);
    return data;
  }

  const programFilterConfig =
    findField('upcoming_programs_v3') || findField('upcoming_programs_v2');

  const onLoadMoreClick = (number = 12) => {
    setMaxShowDataLength(prev => prev + number);
  };

  useEffect(() => {
    if (!userLocation) {
      (async () => {
        let pos = await getLatLong();
        if (pos && pos?.latlong) {
          const [latitude, longitude] = pos?.latlong.split(',') || [];
          const updatedLocation = {
            ...userLocation,
            vpn: { latitude, longitude },
          };
          setUserLocation(updatedLocation);
        } else if (typeof window !== undefined) {
          const { latitude, longitude } =
            JSON.parse(localStorage.getItem('coords')) || {};
          const updatedLocation = {
            ...userLocation,
            vpn: { latitude, longitude },
          };
          setUserLocation(updatedLocation);
        }
      })();
    }
  }, []);

  const handleData = data => {
    let finalData = [];
    let counter = 0;

    while (data[counter]) {
      let tempData = {
        ...data[counter][counter + 1],
      };
      const [lat, lng] = tempData?.latlong?.split(',').map(Number) || ['', ''];
      const language = (programFilterConfig?.languageList?.json || []).find(
        el => el.value === tempData?.lang
      )?.label;
      finalData.push({ ...tempData, longitude: lng, latitude: lat, language });
      counter++;
    }

    return finalData;
  };

  const fetchFilterAsideData = async () => {
    let res;
    try {
      const params = isWavesOfBlissBengaluruPage ? `&state=KA` : `&state=UP`;
      res = await fetchApiAndCache(
        `${config.SCHEDULE_WOB__PROGRAM_API_PATH}${params}`
      );
    } catch (error) {
    } finally {
      setLoading(false);
    }
    const newRes = handleData(res?.results)?.filter(
      card => card?.registration_closed !== 'regClosed'
    );
    setPrograms(newRes);
  };

  useEffect(() => {
    fetchFilterAsideData();
  }, []);

  const handleValueUpdate = (value, key) => {
    if (value) {
      setLastAddedFilterName(key);
    } else {
      setLastAddedFilterName('');
    }
    const restField = {};
    if (['city', 'center'].includes(key) && filterValue?.showNearMe) {
      restField.showNearMe = false;
    } else if (
      ['showNearMe'].includes(key) &&
      (filterValue?.city || filterValue?.center)
    ) {
      restField.city = null;
      restField.center = null;
    }
    setFilterValue(prev => ({
      ...prev,
      [key]: value,
      ...restField,
    }));
  };

  const CustomControl = props => (
    <components.Control {...props}>
      <BiSearch size={20} style={{ marginLeft: 10, color: '#5C5C5C' }} />
      {props.children}
    </components.Control>
  );

  const CustomDropdownIndicator = props => (
    <components.DropdownIndicator {...props}>
      <div
        style={{
          borderLeft: '1px solid #cccccc',
          height: '30px',
          marginRight: '6px',
        }}
      />

      <MdKeyboardArrowDown
        size={24}
        style={{ color: '#64748B', margin: 'auto 0' }}
      />
    </components.DropdownIndicator>
  );

  const Filters = ({ onChange, filterOptions, placeholder, keyName }) => {
    return (
      <Box
        maxWidth={{ base: '100%', md: '242px' }}
        width="100%"
        m={{ base: '0', md: '0 15px' }}
        fontFamily="FedraSansStd-book"
      >
        <Select
          isClearable
          placeholder={placeholder}
          closeMenuOnSelect={true}
          components={{
            Control: CustomControl,
            DropdownIndicator: CustomDropdownIndicator,
            IndicatorSeparator: () => null,
          }}
          isLoading={loading}
          className="basic-single"
          options={filterOptions}
          onChange={value => onChange(value, keyName)}
          value={filterValue[keyName]}
          isSearchable={true}
          name="color"
          styles={{
            control: provided => ({
              ...provided,
              border: 'none',
              borderBottom: '1px solid #AEA493',
              borderRadius: 0,
              boxShadow: 'none',
              minHeight: '48px',
              fontFamily: 'FedraSansStd-book',
              color: '#000000',
            }),
            placeholder: provided => ({
              ...provided,
              color: '#333333',
              fontSize: '14px',
              fontFamily: 'FedraSansStd-book',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }),
            menu: provided => ({
              ...provided,
              borderRadius: 0,
              backgroundColor: 'white',
              boxShadow: '4px 4px 22px 0px #00000040',
              marginTop: '11px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '12px 0 4px 0',
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isFocused ? 'transparent' : 'white',
              color:
                state.isFocused || state.isSelected ? '#AE4511' : '#333333',
              fontWeight: state.isSelected ? 500 : 400,
              cursor: 'pointer',
              padding: '0 12px 0 32px',
              marginBottom: '8px',
              lineHeight: '35px',
              '&:hover': {
                color: '#AE4511',
                backgroundColor: 'transparent !important',
              },
              '&:active': {
                backgroundColor: 'transparent',
              },
            }),
            menuList: provided => ({
              ...provided,
              paddingTop: 0,
              paddingBottom: 0,
            }),
          }}
          theme={t => ({
            ...t,
            colors: {
              ...t.colors,
              primary: '#AE4511',
              primary25: 'transparent',
              primary50: 'transparent',
            },
          })}
        />
      </Box>
    );
  };

  const filteredPrograms = useMemo(() => {
    setMaxShowDataLength(12);
    if (Object.keys(filterValue).every(key => !filterValue[key]))
      return programs;
    const showPrograms = [];

    const locationData = userLocation?.browser?.latitude
      ? userLocation?.browser
      : userLocation?.vpn;

    const showNearPrograms =
      filterValue?.showNearMe &&
      !!Object.keys(locationData)?.length &&
      Object.values(locationData).some(a => a)
        ? orderByDistance({ ...(locationData || {}) }, programs)
        : filterValue?.city?.value || filterValue?.center?.value
        ? programs.filter(program => {
            return (
              (program.city && filterValue?.city?.value === program.city) ||
              (program.locale &&
                program?.locale
                  ?.toLowerCase()
                  .includes(filterValue?.center?.value?.toLowerCase()))
            );
          })
        : programs;

    showNearPrograms.forEach(program => {
      let isMatch = true;
      if (filterValue?.weekdays?.value) {
        isMatch = isMatch && filterValue.weekdays.value === program.fr;
      }
      if (filterValue?.language?.value) {
        isMatch = isMatch && filterValue.language.value === program.lang;
      }
      if (filterValue?.sadhguruSannidhi) {
        isMatch =
          isMatch &&
          program?.address_title?.toLowerCase()?.includes('sadhguru sannidhi');
      }
      if (isMatch) {
        showPrograms.push(program);
      }
    });
    if (lastAddedFilterName) {
      if (['showNearMe', 'sadhguruSannidhi'].includes(lastAddedFilterName)) {
        Mixpanel.track('ie_wave_quick_filter_result', {
          filter_name:
            lastAddedFilterName === 'showNearMe'
              ? 'Show programs near me'
              : 'Show programs at Sadhguru Sannidhi, Bengaluru',
          result_count: showPrograms?.length,
        });
      } else {
        Mixpanel.track('ie_wave_filter_result', {
          location: filterValue?.center?.value,
          date_range: filterValue?.weekdays?.label,
          language: filterValue?.language?.value
            ? (programFilterConfig?.languageList?.json || []).find(
                lang => lang?.value === filterValue?.language?.value
              )?.label
            : '',
          result_count: showPrograms?.length || 0,
        });
      }
    }
    return showPrograms;
  }, [programs, filterValue]);

  const formatDateRange = (from, to) => {
    if (!from || !to) return;
    const options = {
      day: '2-digit',
      month: 'short',
    };

    const fromDate = new Date(from);
    const toDate = new Date(to);

    const format = date => {
      const parts = date.toLocaleDateString('en-GB', options).split(' ');
      const day = parts[0];
      const month = parts[1].slice(0, 3);
      return `${day} ${month}`;
    };

    return {
      label: `${format(fromDate)} - ${format(toDate)}`,
      value: from,
    };
  };

  const fieldOptions = useMemo(() => {
    const centerOptions = [];
    const dateOptions = [];
    programs?.forEach(program => {
      const localValue = program?.locale?.split(',')?.[1]?.trim() || '';
      const isLocalExist = centerOptions.find(
        local => local?.value === localValue
      );
      if (!isLocalExist) {
        centerOptions.push({
          value: localValue,
          label: localValue,
        });
      }
      const dateValue = formatDateRange(program?.fr, program?.to);
      const isDateExist = dateOptions.find(
        date => date?.value === dateValue?.value
      );
      if (!isDateExist) {
        dateOptions.push(dateValue);
      }
    });
    return {
      centerOptions: centerOptions?.sort((a, b) =>
        a?.label.localeCompare(b?.label)
      ),
      dateOptions: dateOptions?.sort(
        (a, b) => new Date(a?.value).getTime() - new Date(b?.value).getTime()
      ),
    };
  }, [programs]);

  const onNearMeClick = e => {
    if (!userLocation?.browser && navigator?.geolocation) {
      // setting browser with api data
      (async () => {
        let pos = await getLatLong();
        const [latitude, longitude] = pos?.latlong.split(',') || [];
        setUserLocation({
          ...userLocation,
          browser: { latitude, longitude },
        });
      })();
      // setting location based on browser
      navigator?.geolocation?.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords || {};
          setUserLocation({
            ...userLocation,
            browser: { latitude, longitude },
          });
          localStorage.setItem(
            'coords',
            JSON.stringify({
              latitude: latitude || '',
              longitude: longitude || '',
            })
          );
        },
        error => {
          console.log(error);
        }
      );

      handleValueUpdate(e.target.checked, 'showNearMe');
    } else if (userLocation?.browser?.latitude) {
      setUserLocation(prev => {
        delete prev.browser;
        return prev;
      });
      handleValueUpdate(e.target.checked, 'showNearMe');
    } else {
      console.log('GeoLocation is not supported in this browser');
    }
  };

  const showDataList = filteredPrograms?.slice(0, maxShowDataLength);

  return (
    <Box
      w={{ base: '100%', sm: '450px', md: '100%' }}
      px={{ base: '16px', sm: '20px', md: '30px', xl: '16px' }}
      pt={{ base: '58px', md: '80px', lg: '100px' }}
      mx="auto"
      id="ieo-wave-program-registration"
      pb={'0px'}
    >
      <SingleLineText
        pb={{ base: '20px', lg: '30px' }}
        display="flex"
        justifyContent="center"
        alignItems="center"
        mx="auto"
        sectionContent={{ text: findField('section_title')?.titleText }}
        region={region}
        lang={lang}
        {...ResearchIntroSt[lang]['wavesOfBliss']}
        my="0px"
      />
      <StructuredTextParser
        className="faqAnswer"
        maxW="1000px"
        textAlign="center"
        fontFamily="FedraSansStd-book"
        fontSize={{ base: '14px', md: '20px' }}
        lineHeight={{ base: '17px', md: '35px' }}
        mb={{ base: '30px', md: '60px' }}
        fontWeight="400"
        str={render(sectionContent[1]?.body)}
        region={region}
        lang={lang}
        color="#000000"
      />

      <Box w="100%">
        <Box w={{ base: '100%' }} mb="25px">
          <Box
            fontFamily="FedraSansStd-medium, sans-serif"
            fontSize="14px"
            lineHeight={{
              base: '35px',
              md: '40px',
            }}
            textAlign="left"
            display="flex"
            flexWrap={{ base: 'wrap', md: 'wrap' }}
            alignItems={{ base: 'flex-start', md: 'center' }}
            justifyContent={{ base: 'flex-start', md: 'center' }}
            mx={'auto'}
            w={'100%'}
            rowGap={'9px'}
          >
            <Box
              as="label"
              fontWeight="400"
              fontSize="14px"
              fontFamily="FedraSansStd-book"
              color={'#333333'}
              placeContent="center"
              mr={'7px'}
              display={{ base: 'none', lg: 'grid' }}
            >
              {upcomingPrograms?.filter || 'FILTERS:'}
            </Box>

            {programFilterConfig?.cityFilterVisibility && (
              <Filters
                key="city"
                placeholder={upcomingPrograms?.city || 'Select City'}
                filterName={'city'}
                filterOptions={programFilterConfig?.cityList?.json || []}
                onChange={handleValueUpdate}
                keyName="city"
              />
            )}

            {programFilterConfig?.locationFilterVisibility && (
              <Filters
                key="center"
                placeholder={upcomingPrograms?.location || 'Select Location'}
                filterName={'center'}
                filterOptions={fieldOptions?.centerOptions || []}
                onChange={handleValueUpdate}
                keyName="center"
              />
            )}

            <Filters
              key="Weekend"
              placeholder={upcomingPrograms?.weekend || 'Select Weekend'}
              filterName={lang}
              filterOptions={fieldOptions?.dateOptions || []}
              onChange={handleValueUpdate}
              keyName="weekdays"
            />
            {programFilterConfig?.languageFilterVisibility && (
              <Filters
                key="language"
                placeholder={upcomingPrograms?.lang || 'Select Language'}
                filterName={lang}
                filterOptions={programFilterConfig?.languageList?.json || []}
                onChange={handleValueUpdate}
                keyName="language"
              />
            )}
          </Box>
        </Box>
        {(programFilterConfig?.nearbyProgramsCheckboxVisibility ||
          programFilterConfig?.programsSadhguruSannidhiBengaluruCheckboxVisibility) && (
          <Flex
            columnGap={{ base: '10px', md: '47px' }}
            rowGap={{ base: '10px', md: '10px' }}
            alignItems={{ base: 'flex-start', md: 'center' }}
            flexDirection={{ base: 'column', md: 'row' }}
            justifyContent={{
              base: 'flex-start',
              md: 'center',
            }}
            mb={{
              base: '40px',
            }}
            mx={'auto'}
            maxW={'1146px'}
          >
            {programFilterConfig?.nearbyProgramsCheckboxVisibility && (
              <Box
                as="div"
                textAlign="left"
                fontFamily="FedraSansStd-book"
                fontSize="16px"
                color="#676055"
                lineHeight={'24px'}
              >
                <Checkbox
                  borderColor="#535353"
                  isChecked={filterValue?.showNearMe}
                  onChange={onNearMeClick}
                >
                  {upcomingPrograms?.nearme || 'Show programs near me'}
                </Checkbox>
              </Box>
            )}
            {programFilterConfig?.programsSadhguruSannidhiBengaluruCheckboxVisibility && (
              <Box
                as="div"
                textAlign="left"
                fontFamily="FedraSansStd-book"
                fontSize="16px"
                color="#676055"
                lineHeight={'24px'}
              >
                <Checkbox
                  borderColor="#535353"
                  isChecked={filterValue?.sadhguruSannidhi}
                  onChange={e =>
                    handleValueUpdate(e.target.checked, 'sadhguruSannidhi')
                  }
                >
                  {upcomingPrograms?.nearssb}
                </Checkbox>
              </Box>
            )}
          </Flex>
        )}

        {!showDataList.length && loading ? (
          <Box margin="20px">
            <Loader key="upcoming-null" pos="relative" />
          </Box>
        ) : null}
        {showDataList?.length > 0 ? (
          <>
            <Box
              fontSize={{ base: '13px', md: '18px' }}
              lineHeight={'18px'}
              fontFamily="FedraSansStd-book"
              textAlign={{ base: 'left', md: 'center' }}
              mb={{
                base: '16px',
                md: '40px',
              }}
              color={'#272727'}
              mx={{ base: '0', md: 'auto' }}
              maxW={'1146px'}
            >
              {upcomingPrograms?.showingProg
                .replace('##num##', showDataList?.length || 0)
                .replace('##total##', filteredPrograms?.length || 0)}
            </Box>
            <SimpleGrid
              columnGap={{ base: '29px', md: '24px' }}
              rowGap={{ base: '29px', md: '24px' }}
              columns={{
                base: 1,
                md: 2,
                lg: 3,
                xl: 4,
              }}
              width={{ base: 'auto', lg: 'auto' }}
              m={'0 auto'}
              px={'0px'}
              maxW={'1328px'}
            >
              {showDataList?.length > 0 &&
                showDataList?.map((program, index) => {
                  return (
                    <Box
                      display="grid"
                      gridGap="15px"
                      maxW={302}
                      width="100%"
                      key={index}
                      mx="auto"
                      h={'100%'}
                      _hover={{
                        boxShadow: '0 0 48px 0 rgb(0 0 0 / 25%)',
                      }}
                    >
                      <UpcomingProgramCard
                        key={index}
                        count={12}
                        sectionContent={program}
                        apiType={undefined}
                        region={region}
                        lang={lang}
                        LinkInNewTab={
                          programFilterConfig?.cardClickOpensRegistrationLinkInNewTab
                        }
                        programtype={
                          programFilterConfig?.title ||
                          programFilterConfig?.titleText
                        }
                      />
                    </Box>
                  );
                })}
            </SimpleGrid>
            {(showDataList?.length || 0) < filteredPrograms?.length && (
              <SimpleButton
                id={'load-more-button'}
                minW="217px"
                background={'#F37021 !important'}
                h={{ base: '48px' }}
                textAlign="center"
                mb={'0'}
                mt={{ base: '30px', md: '60px' }}
                mx={{ base: 'auto', lg: 'auto' }}
                _hover={{
                  background: '#BC570F !important',
                }}
                borderRadius={'50px !important'}
                style={{ buttonStyle: 'Orange', buttonText: 'Load More' }}
                onClick={() => {
                  onLoadMoreClick();
                }}
                padding={{
                  base: '10px 29px',
                }}
                lineHeight={{
                  base: '28px',
                }}
                fontSize="20px"
                fontWeight="500"
                fontFamily="FedraSansStd-medium"
                zIndex={100}
              ></SimpleButton>
            )}
          </>
        ) : (
          !loading && (
            <Box
              w="100%"
              pt={{ base: '0px', md: '60px' }}
              textAlign={{ base: 'start', md: 'center' }}
              fontFamily="FedraSansStd-Book"
              color="#AE4511"
              lineHeight={{ base: '24px', md: '24px' }}
              fontSize={{ base: '16px', md: '24px' }}
            >
              {StructuredTextContent(
                programFilterConfig?.programNotFoundMessage
              )}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default ProgramList;
